/* Port of Han button-sweep.js; Shopify hosts and inert clone safety adapters. */
(() => {
  'use strict';
  if (window.__hanButtonMotion) return;
  window.__hanButtonMotion = true;
  const root = document.documentElement;
  const candidates = '.text-link,.han-text-link,.collections-inline-link,.han-hero-copy a,.popular-products .section-heading__button';
  const solid = '[data-han-solid-sweep]'; // Opt-in only; no native purchase/filter handler is replaced.
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const records = new Map();
  const proxyRecords = new Map();
  const dirty = new Set();
  let queued = false;
  const svgNamespace = 'http://www.w3.org/2000/svg';
  const interactive = new Set(['a','button','input','select','textarea','summary','select','option','optgroup','form','fieldset','label','iframe','object','embed']);
  const layout = ['display','flex-direction','flex-wrap','align-items','align-content','justify-content','gap','column-gap','row-gap','padding-top','padding-right','padding-bottom','padding-left','text-align','text-indent','white-space','word-spacing','box-sizing'];
  const childLayout = [...layout,'font-family','font-size','font-weight','font-style','font-stretch','font-variant','line-height','letter-spacing','text-transform','vertical-align','margin-top','margin-right','margin-bottom','margin-left','flex-grow','flex-shrink','flex-basis','align-self','min-width','max-width','position','top','right','bottom','left'];
  const elementFor = node => node?.nodeType === 1 ? node : node?.parentElement;
  const inOverlay = node => Boolean(elementFor(node)?.closest('.button-sweep__overlay'));
  const eligible = host => host instanceof HTMLElement && !inOverlay(host) && (host.matches(solid) || (root.dataset.glassButtons !== 'false' && (host.closest('#MainContent') || host.closest('.cart-empty') || host.matches('.spotlight__preview-link'))));
  function copyStyle(source,destination,properties) {
    const style = getComputedStyle(source);
    properties.forEach(property => {
      const value = style.getPropertyValue(property);
      if (value) destination.style.setProperty(property,value,'important');
    });
    return style;
  }
  function cloneContent(source) {
    if (source.nodeType === 3) return document.createTextNode(source.nodeValue || '');
    if (source.nodeType !== 1) return null;
    const tag = source.tagName.toLowerCase();
    const neutral = interactive.has(tag) && source.namespaceURI !== svgNamespace;
    const clone = neutral ? document.createElement('span') : source.cloneNode(false);
    if (neutral) source.getAttributeNames().forEach(name => clone.setAttribute(name,source.getAttribute(name)));
    for (const name of clone.getAttributeNames()) {
      if (name === 'id' || /^(?:value|checked|selected|disabled|required|action|method|enctype|autocomplete|multiple|readonly)$/.test(name) || name.startsWith('data-') || name.startsWith('aria-') || name.startsWith('on') || /^(?:tabindex|href|xlink:href|target|rel|name|for|role|type|form|autofocus|contenteditable|draggable|accesskey)$/.test(name)) clone.removeAttribute(name);
    }
    const style = copyStyle(source,clone,childLayout);
    if (tag === 'svg' || tag === 'img') {
      ['width','height'].forEach(property => clone.style.setProperty(property,style.getPropertyValue(property),'important'));
    }
    if (source.namespaceURI === svgNamespace) {
      clone.setAttribute('focusable','false');
      ['stroke-width','stroke-linecap','stroke-linejoin','stroke-dasharray','stroke-dashoffset','fill-rule','vector-effect'].forEach(property => {
        const value = style.getPropertyValue(property);
        if (value) clone.style.setProperty(property,value,'important');
      });
      ['fill','stroke'].forEach(property => {
        const value = style.getPropertyValue(property);
        if (value) clone.style.setProperty(property,value === 'none' ? 'none' : '#171717','important');
      });
    }
    for (const child of source.childNodes) {
      const copied = cloneContent(child);
      if (copied) clone.append(copied);
    }
    if (tag === 'input' && source.value) clone.textContent = source.value;
    return clone;
  }
  function proxiesFor(host) {
    if (host.classList.contains('film-hero__cta')) {
      const link = host.closest('.split-video-hero')?.querySelector('.film-hero__video-link');
      if (link) return [link];
    }
    return [host.closest('a,button') || host];
  }
  function refresh(record) {
    if (!record.host.isConnected || !eligible(record.host)) {remove(record);return;}
    const children = [...record.host.childNodes].filter(node => node !== record.overlay);
    const cloned = children.map(cloneContent).filter(Boolean);
    copyStyle(record.host,record.overlay,layout);
    record.overlay.replaceChildren(...cloned);
    if (record.overlay.parentElement !== record.host) record.host.append(record.overlay);
  }
  function prepare(host) {
    if (!eligible(host)) return;
    const existing = records.get(host);
    if (existing) return existing;
    const overlay = document.createElement('span');
    overlay.className = 'button-sweep__overlay';
    overlay.setAttribute('aria-hidden','true');overlay.setAttribute('inert','');overlay.inert = true;
    const record = {host,overlay,proxies:proxiesFor(host),position:null};
    records.set(host,record);
    if (getComputedStyle(host).position === 'static') {
      record.position = {value:host.style.getPropertyValue('position'),priority:host.style.getPropertyPriority('position')};
      host.style.setProperty('position','relative');
    }
    host.setAttribute('data-button-sweep','');
    record.proxies.forEach(proxy => {
      if (!proxyRecords.has(proxy)) proxyRecords.set(proxy,new Set());
      proxyRecords.get(proxy).add(record);
    });
    refresh(record);
    return record;
  }
  function remove(record) {
    records.delete(record.host);dirty.delete(record);
    record.proxies.forEach(proxy => {
      const set = proxyRecords.get(proxy);set?.delete(record);
      if (!set?.size) proxyRecords.delete(proxy);
    });
    record.overlay.remove();
    ['data-button-sweep','data-button-sweep-hover','data-button-sweep-focus'].forEach(name => record.host.removeAttribute(name));
    if (record.position && record.host.style.getPropertyValue('position') === 'relative') {
      record.position.value ? record.host.style.setProperty('position',record.position.value,record.position.priority) : record.host.style.removeProperty('position');
    }
  }
  function discover(scope) {
    if (scope.nodeType !== 1 || inOverlay(scope)) return;
    if (scope.matches(candidates)) prepare(scope);
    scope.querySelectorAll(candidates).forEach(prepare);
  }
  function syncAll() {
    [...records.values()].forEach(record => {if (!record.host.isConnected || !eligible(record.host)) remove(record);});
    document.querySelectorAll(candidates).forEach(prepare);
    records.forEach(refresh);
  }
  function queueRefresh(record) {
    dirty.add(record);
    if (queued) return;
    queued = true;
    Promise.resolve().then(() => {
      queued = false;
      const batch = [...dirty];dirty.clear();
      batch.forEach(record => {if (records.get(record.host) === record) refresh(record);});
    });
  }
  function forTarget(node,callback) {
    const seen = new Set();
    let element = elementFor(node);
    while (element) {
      proxyRecords.get(element)?.forEach(record => {if (!seen.has(record)) {seen.add(record);callback(record,element);}});
      element = element.parentElement;
    }
  }
  function clearHover() {records.forEach(record => record.host.removeAttribute('data-button-sweep-hover'));}
  function initialize() {
    syncAll();
    document.addEventListener('pointerover',event => {
      if (!fine.matches || event.pointerType === 'touch') return;
      forTarget(event.target,(record,proxy) => {
        if (event.relatedTarget && proxy.contains(event.relatedTarget)) return;
        refresh(record);
        if (!record.host.disabled) record.host.setAttribute('data-button-sweep-hover','');
      });
    });
    document.addEventListener('pointerout',event => forTarget(event.target,(record,proxy) => {
      if (!event.relatedTarget || !proxy.contains(event.relatedTarget)) record.host.removeAttribute('data-button-sweep-hover');
    }));
    document.addEventListener('pointerdown',event => {if (event.pointerType === 'touch') clearHover();});
    document.addEventListener('focusin',event => forTarget(event.target,(record,proxy) => {
      refresh(record);
      if (proxy.matches(':focus-visible') && !record.host.disabled) record.host.setAttribute('data-button-sweep-focus','');
    }));
    document.addEventListener('focusout',event => forTarget(event.target,record => record.host.removeAttribute('data-button-sweep-focus')));
    window.addEventListener('blur',clearHover);
    window.addEventListener('scroll',clearHover,{capture:true,passive:true});
    window.addEventListener('pagehide',clearHover);
    document.addEventListener('pointercancel',clearHover);
    document.addEventListener('visibilitychange',() => {if (document.hidden) clearHover();});
    fine.addEventListener('change',() => {if (!fine.matches) clearHover();});
    ['sttoke:appearance-change','sttoke:type-change','shopify:section:load','shopify:section:unload','variant:change','variant-change'].forEach(name => document.addEventListener(name,syncAll));
    window.addEventListener('resize',() => records.forEach(queueRefresh),{passive:true});
    document.fonts?.ready.then(() => records.forEach(queueRefresh));
    if (typeof MutationObserver !== 'undefined') new MutationObserver(changes => {
      changes.forEach(change => {
        if (inOverlay(change.target)) return;
        const changed = [...(change.addedNodes || []),...(change.removedNodes || [])];
        if (change.type === 'childList' && changed.length && changed.every(node => node.nodeType === 1 && node.classList.contains('button-sweep__overlay'))) return;
        const element = elementFor(change.target);
        const host = element?.closest('[data-button-sweep]');
        if (host && records.has(host)) queueRefresh(records.get(host));
        else if (change.type === 'attributes' && element?.matches(candidates)) prepare(element);
        if (change.type === 'childList' && !host) [...change.addedNodes].forEach(node => {if (node.nodeType === 1) discover(node);});
      });
      [...records.values()].forEach(record => {if (!record.host.isConnected) remove(record);});
    }).observe(document.body,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['hidden','class','style','disabled']});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',initialize,{once:true});
  else initialize();
})();
